import { useCallback, useRef, MouseEvent, createContext, useState, useEffect } from "react";
import { Box } from "@mui/material";
import Autoplay from "embla-carousel-autoplay";
import useEmblaCarousel from "embla-carousel-react";
import { useRouter } from "next/router";
import { ContainerHeader } from "src/components-mui/layout/container";
import { HeroSliderNav } from "src/components-mui/modules/hero-module/components/hero-slider-nav";
import { HeroSliderSlide, IHeroSliderSlideProps } from "src/components-mui/modules/hero-module/components/hero-slider-slide";
import { getEmblaDelays } from "src/components-mui/modules/hero-module/utils/embla-delay";
import { HERO_SLIDER_SLIDE_CLASS } from "./constants";

interface IHeroSliderContext {
    setIsInputFocused: (state: boolean) => void;
}

export const HeroSliderContext = createContext<IHeroSliderContext>({ setIsInputFocused: () => {} });

const DATA_TEST_ID = "hero-slider-module";

export interface IHeroSliderModuleProps {
    slides: IHeroSliderSlideProps[];
}

export const HeroSliderModule = ({ slides }: IHeroSliderModuleProps) => {
    const router = useRouter();
    const [isHovered, setIsHovered] = useState(false);
    const [isInputFocused, setIsInputFocused] = useState(false);

    const getDelay = (scrollSnaps: number[]): number[] => {
        return getEmblaDelays(scrollSnaps, slides);
    };

    const autoplayInstance = useRef(Autoplay({ delay: getDelay }));
    const enableSliderFeatures = slides.length > 1;
    const shouldAutoplay = enableSliderFeatures && !isHovered && !isInputFocused;
    const slideIndexFromQuery = slides.findIndex(slide => slide.slideId !== "" && slide.slideId === router.query.slide);
    const startIndex = slideIndexFromQuery > 0 ? slideIndexFromQuery : 0;

    const [emblaRef, embla] = useEmblaCarousel(
        {
            loop: true,
            watchDrag: enableSliderFeatures,
            startIndex,
        },
        [autoplayInstance.current]
    );

    const pauseAutoplay = useCallback(() => {
        setIsHovered(true);
    }, []);

    const resumeAutoplay = useCallback((event: MouseEvent) => {
        if (!(event.target instanceof HTMLElement)) {
            return;
        }

        const isTargetSlideOrChild = Boolean(event.target.closest(`.${HERO_SLIDER_SLIDE_CLASS}`));
        if (isTargetSlideOrChild) {
            setIsHovered(false);
        }
    }, []);

    useEffect(() => {
        if (!embla || !autoplayInstance.current) {
            return;
        }

        if (shouldAutoplay) {
            autoplayInstance.current.play();
        } else {
            autoplayInstance.current.stop();
        }
    }, [shouldAutoplay, embla]);

    if (slides.length === 0) {
        return null;
    }

    return (
        <HeroSliderContext.Provider value={{ setIsInputFocused }}>
            <ContainerHeader
                data-test-id={DATA_TEST_ID}
                sx={{ position: "relative", height: ["720px", "720px", "650px", "760px"] }}
                onMouseEnter={pauseAutoplay}
                onMouseMove={pauseAutoplay}
                onMouseLeave={resumeAutoplay}
            >
                <Box
                    ref={enableSliderFeatures ? emblaRef : undefined}
                    sx={{
                        overflow: "hidden",
                        width: "100%",
                        height: "100%",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            width: "100%",
                            height: "100%",
                        }}
                    >
                        {slides.map((slide, i) => (
                            <HeroSliderSlide key={i} {...slide} />
                        ))}
                    </Box>
                </Box>
                {enableSliderFeatures && embla ? <HeroSliderNav embla={embla} autoplayInstance={autoplayInstance.current} /> : null}
            </ContainerHeader>
        </HeroSliderContext.Provider>
    );
};
